// Helpers PUROS de la Economía del cliente (bloque `economy` del Panel, pantalla de carga, modal de
// objetivo y vista del Listado). Sin React: el copy sale por el `t` que recibe cada uno.
//
// No formatean números por su cuenta: montos por `formatFullMoney` / `formatCompactMoney` y ratios
// por `formatGoalValue` (`panelFormatters`). Acá sólo se les pega la unidad («×», «%»), que es lo que
// no existía. El backend ya redondea al serializar (dinero a entero, % y ROAS a 1 decimal): el FE no
// vuelve a redondear, así la card, el listado y el modal muestran EL MISMO número.

import {formatFullMoney, formatGoalValue, parsePanelDate} from './panelFormatters';

// Mínimo de días con inversión y ventas para estimar el tope (contrato §2, `TOO_FEW_DAYS`).
export const SPEND_CAP_MIN_DAYS = 30;

export const SOURCE_LABEL_KEYS = {
    MANUAL: 'client_panel.economy.source.MANUAL',
    CSV: 'client_panel.economy.source.CSV',
    ESCALAFY: 'client_panel.economy.source.ESCALAFY',
    STORE_COGS: 'client_panel.economy.source.STORE_COGS',
};

// Los cinco datos del perfil (más el ticket) en el orden en que se muestran y se cargan.
export const PROFILE_FIELDS = [
    'avg_ticket',
    'gross_margin_pct',
    'shipping_per_order',
    'payment_fee_pct',
    'fixed_costs_monthly',
    'agency_fee_monthly',
];

// Cuáles son porcentajes (el resto es dinero en la moneda del perfil).
export const PERCENT_FIELDS = new Set(['gross_margin_pct', 'payment_fee_pct']);

/** `2,7×`. `null`/no numérico → `null`. */
export function formatRoas(value) {
    const text = formatGoalValue(value);
    return text === null ? null : `${text}×`;
}

/** `37,1 %`. `null`/no numérico → `null`. */
export function formatPct(value) {
    const text = formatGoalValue(value);
    return text === null ? null : `${text} %`;
}

/**
 * Una ganancia con su signo a la vista: `+ARS 1.152.000` / `-ARS 84.000`. Una pérdida sin el signo
 * adelante se lee como ganancia: la moneda va primero y el «-» quedaba en el medio.
 */
export function formatSignedMoney(currency, amount) {
    if (amount === null || amount === undefined || amount === '') return null;
    const value = Number(amount);
    if (!Number.isFinite(value)) return null;
    const text = formatFullMoney(currency, Math.abs(value));
    if (Math.round(value) === 0) return text;
    return `${value > 0 ? '+' : '-'}${text}`;
}

/** Un valor del perfil con su unidad: porcentaje o dinero en la moneda del perfil. */
export function formatProfileValue(field, value, currency) {
    if (PERCENT_FIELDS.has(field)) return formatPct(value);
    return formatFullMoney(currency, value);
}

/**
 * «12 de septiembre» (o «September 12»). El año sólo si no es el actual: casi todas las versiones
 * son de este año y repetirlo alarga cada renglón. `YYYY-MM-DD` se lee como día LOCAL.
 */
export function formatEconomyDate(value, language, now = new Date()) {
    const date = parsePanelDate(value);
    if (!date) return null;
    const locale = String(language || '').startsWith('en') ? 'en-US' : 'es-AR';
    const options = {day: 'numeric', month: 'long'};
    if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
    return date.toLocaleDateString(locale, options);
}

/** «12 sep»: la versión corta, para celdas del Listado. */
export function formatEconomyShortDate(value, language, now = new Date()) {
    const date = parsePanelDate(value);
    if (!date) return null;
    const locale = String(language || '').startsWith('en') ? 'en-US' : 'es-AR';
    const options = {day: 'numeric', month: 'short'};
    if (date.getFullYear() !== now.getFullYear()) options.year = '2-digit';
    return date.toLocaleDateString(locale, options).replace('.', '');
}

// El desajuste de moneda viene de una CUENTA de la línea, no de la línea (contrato, QA-ecob-N03).
export const MISMATCH_ACCOUNT_VS_LINE = 'ACCOUNT_VS_LINE';

/** El motivo por el que un campo de `derived`/`client_totals` no se pudo calcular (o `null`). */
export function reasonFor(block, field) {
    const reasons = (block && block.unavailable_reason) || {};
    const reason = reasons[field];
    return reason ? String(reason) : null;
}

/**
 * El texto de un `unavailable_reason` del contrato (§2). Los códigos que traen dato lo llevan
 * después de «:» (`TOO_FEW_DAYS:21`, `missing_input:gross_margin_pct`).
 *
 * Un código que este archivo no sabe nombrar devuelve `null`: el tile muestra «—» y nada más, en
 * vez de un texto inventado o la clave cruda.
 */
export function economyReasonText(reason, t, {
    lineCurrency, profileCurrency, mismatchKind, accountCurrencies, totals = false,
} = {}) {
    if (!reason) return null;
    const [code, detail] = String(reason).split(':');
    switch (code) {
        case 'NO_STORE':
        case 'NO_ORDERS_90D':
        case 'UNSTABLE_FIT':
        case 'MIXED_CURRENCY':
        case 'NON_POSITIVE_MARGIN':
        case 'SHARED_STORE':
        case 'PENDING':
            // `SHARED_STORE`: la misma tienda en varias líneas con márgenes distintos (no hay UNA
            // ganancia). `PENDING` (Listado): la caché de la ganancia se está calculando; no es «sin tienda».
            return t(`client_panel.economy.unavailable.${code}`);
        case 'TOO_FEW_DAYS': {
            const days = Number(detail);
            return t('client_panel.economy.unavailable.TOO_FEW_DAYS', {
                needed: SPEND_CAP_MIN_DAYS,
                days: Number.isFinite(days) ? days : 0,
            });
        }
        case 'CURRENCY_MISMATCH':
            // En los totales del cliente: todas las líneas con tienda quedaron afuera por la moneda
            // (QA-ecob-N02). Cuál y cómo se arregla lo dice la nota de cada línea, no el tile.
            if (totals) return t('client_panel.economy.unavailable.CURRENCY_MISMATCH_TOTALS');
            // Una CUENTA de la línea en otra moneda: personalizar la línea no lo arregla (QA-ecob-N03).
            if (mismatchKind === MISMATCH_ACCOUNT_VS_LINE) {
                const others = (accountCurrencies || []).filter((code) => code && code !== lineCurrency);
                return t('client_panel.economy.unavailable.CURRENCY_MISMATCH_ACCOUNT', {
                    account_currency: others.join(', ') || '—',
                    line_currency: lineCurrency || '—',
                    interpolation: {escapeValue: false},
                });
            }
            return t('client_panel.economy.unavailable.CURRENCY_MISMATCH', {
                line_currency: lineCurrency || '—',
                profile_currency: profileCurrency || '—',
                interpolation: {escapeValue: false},
            });
        case 'missing_input': {
            if (!detail) return null;
            return t('client_panel.economy.unavailable.missing_input', {
                field: t(`client_panel.economy.input.${detail}`),
                interpolation: {escapeValue: false},
            });
        }
        default:
            return null;
    }
}

/**
 * Por qué una línea no tiene `derived`: el backend lo dice en `unavailable_reason.derived` (con otra
 * moneda que el perfil viaja `CURRENCY_MISMATCH`). Sin motivo explícito devuelve `null`: una línea
 * sin `derived` también puede ser una línea sin perfil (sólo overrides en otras), y atribuirle un
 * desajuste de moneda sería inventarlo (R-ecofe-18).
 */
export function lineDerivedReason(line) {
    const reason = line && line.unavailable_reason;
    if (typeof reason === 'string' && reason) return reason;
    if (reason && typeof reason === 'object' && reason.derived) return String(reason.derived);
    return null;
}

/**
 * Las monedas con que se explica el desajuste de UNA línea: el backend manda `line_currency` y
 * `profile_currency` (con override, la del perfil es la del override; y el desajuste puede venir de
 * una CUENTA de la línea). Sin ellos, la moneda de la línea y la del perfil del cliente.
 * `mismatch_kind` y `account_currencies` dicen si el desajuste es de la línea o de una cuenta.
 */
export function lineCurrencies(line, profileCurrency) {
    return {
        lineCurrency: (line && (line.line_currency || line.currency)) || null,
        profileCurrency: (line && line.profile_currency) || profileCurrency || null,
        mismatchKind: (line && line.mismatch_kind) || null,
        accountCurrencies: (line && Array.isArray(line.account_currencies)) ? line.account_currencies : [],
    };
}

// La única métrica que la sugerencia sabe fijar (contrato §3.4): ROAS sobre las ventas de la tienda.
export const SUGGESTION_METRIC = 'ROAS (Ecommerce)';

/**
 * ¿Tiene sentido ofrecer el objetivo sugerido a una línea? Sólo si su objetivo actual es ese ROAS (o
 * no tiene) y la línea tiene tienda. A una línea que mide CPA, «Usar sugerido» le cambiaría la
 * métrica sin explicar por qué; y sin tienda no hay ventas contra las cuales medir ese ROAS
 * (R-ecofe-01). Es el criterio mínimo; cambiar de métrica, si se quiere ofrecer, lo decide fede.
 */
export function suggestionAppliesTo({metricName, hasStore}) {
    if (hasStore === false) return false;
    return metricName === null || metricName === undefined || metricName === '' || metricName === SUGGESTION_METRIC;
}

/** La entrada de `economy.lines[]` de una línea, por id (o `null`). */
export function economyLineFor(economy, businessUnitId) {
    const lines = (economy && Array.isArray(economy.lines)) ? economy.lines : [];
    return lines.find((line) => line && line.business_unit_id === businessUnitId) || null;
}

/**
 * ¿La fila de la línea ofrece el objetivo sugerido? Sólo si el backend lo calculó, NO coincide con el
 * objetivo actual (`matches_suggestion === false` explícito: un backend viejo que no manda el campo
 * no dispara un chip sobre todas las líneas) y aplica a esa línea (`suggestionAppliesTo`): el backend
 * manda `suggested` en TODA línea con derivados, también en las que miden CPA o no tienen tienda.
 */
export function lineOffersSuggestion(lineEconomy) {
    const goal = lineEconomy && lineEconomy.goal;
    if (!goal) return false;
    const suggested = Number(goal.suggested);
    return goal.suggested !== null && goal.suggested !== undefined && Number.isFinite(suggested)
        && suggested > 0 && goal.matches_suggestion === false
        && suggestionAppliesTo({metricName: goal.metric_name, hasStore: lineEconomy.has_store});
}
